import { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import * as icons from "react-bootstrap-icons";
import {
  RECIPIENT_LIMIT,
  atRecipientLimit,
  bulkLinks,
  deselectResults,
  filterPlayers,
  highlightSegments,
  listedLabel,
  selectResults,
  toggleSelected,
  triggerLabel,
} from "../../utils/helper/broadcasts";

/** A name or an email with the search's matches highlighted. */
const Highlighted = ({ text, term }) =>
  highlightSegments(text, term).map((segment, index) =>
    segment.match ? <mark key={index}>{segment.text}</mark> : <span key={index}>{segment.text}</span>
  );

/**
 * MG-014 / AD-013 (#167, #168): the Recipients field of Send Broadcast - a dropdown listing the
 * players the caller may email, with one search over name and email, checkboxes, Select All /
 * Deselect All (which apply to the search results only while searching), the selected count and Done.
 * The selection stops at RECIPIENT_LIMIT: Select All fills it in list order, and once it is full the
 * unticked players cannot be ticked.
 *
 * @param {{
 *   players: {playerId: number, playerName: string, emailId: string}[],
 *   selected: Set<number>,
 *   onChange: (selected: Set<number>) => void,
 *   disabled?: boolean,
 *   noPlayers?: boolean,
 *   invalid?: boolean,
 * }} props `noPlayers`: the list has loaded and is empty (the design's State 10).
 */
export const RecipientPicker = ({ players, selected, onChange, disabled = false, noPlayers = false, invalid = false }) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const searchRef = useRef(null);

  const listed = useMemo(() => filterPlayers(players, term), [players, term]);
  const links = bulkLinks(selected, listed, term);
  const label = triggerLabel(selected.size, players.length);
  const full = atRecipientLimit(selected);
  const shown = open && !disabled;
  const Chevron = shown ? icons.ChevronUp : icons.ChevronDown;

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  return (
    <Dropdown
      show={shown}
      onToggle={(next) => setOpen(next)}
      autoClose="outside"
      className="broadcast-picker"
    >
      <Dropdown.Toggle
        as="button"
        type="button"
        id="broadcast-recipients"
        disabled={disabled}
        className={`broadcast-select${invalid ? " is-invalid" : ""}${shown ? " open" : ""}`}
        aria-invalid={invalid || undefined}
      >
        {noPlayers ? (
          <span className="broadcast-placeholder">No players available</span>
        ) : label ? (
          <span>{label}</span>
        ) : (
          <span className="broadcast-placeholder">Select players</span>
        )}
        <Chevron className="broadcast-select-chevron" aria-hidden="true" />
      </Dropdown.Toggle>

      <Dropdown.Menu className="broadcast-picker-menu" aria-label="Select players">
        <div className="broadcast-picker-search">
          <icons.Search aria-hidden="true" />
          <input
            ref={searchRef}
            type="text"
            className="form-control"
            placeholder="Search by name or email"
            aria-label="Search by name or email"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>

        <div className="broadcast-picker-actions">
          <span>{listedLabel(listed.length, players.length, term)}</span>
          <span className="broadcast-picker-links">
            <button
              type="button"
              className="broadcast-link"
              disabled={links.selectDisabled}
              onClick={() => onChange(selectResults(selected, listed))}
            >
              {links.selectLabel}
            </button>
            <button
              type="button"
              className="broadcast-link"
              disabled={links.deselectDisabled}
              onClick={() => onChange(deselectResults(selected, listed))}
            >
              {links.deselectLabel}
            </button>
          </span>
        </div>

        <div className="broadcast-picker-list">
          {listed.length === 0 ? (
            <div className="broadcast-picker-none">No players match “{term.trim()}”.</div>
          ) : (
            listed.map((player) => {
              const checked = selected.has(player.playerId);
              return (
                <label key={player.playerId} className={checked ? "on" : undefined}>
                  <input
                    type="checkbox"
                    className="form-check-input broadcast-check"
                    checked={checked}
                    disabled={!checked && full}
                    onChange={() => onChange(toggleSelected(selected, player.playerId))}
                  />
                  <span>
                    <span className="broadcast-picker-name">
                      <Highlighted text={player.playerName} term={term} />
                    </span>
                    <span className="broadcast-picker-email">
                      <Highlighted text={player.emailId} term={term} />
                    </span>
                  </span>
                </label>
              );
            })
          )}
        </div>

        <div className="broadcast-picker-footer">
          <span>
            <strong>{selected.size}</strong> / {RECIPIENT_LIMIT} selected
          </span>
          <button type="button" className="broadcast-btn broadcast-btn-primary small" onClick={() => setOpen(false)}>
            Done
          </button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};
